import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { getBookLanguageFlagFilename, LANGUAGE_ENTRIES } from '../../../utils/globals';
import { bookLanguageFlagPublicUrl } from './bookLanguageFlags';

const knownCodes = new Set<string>(LANGUAGE_ENTRIES.map((e) => e.code));

type Entry = { code: string; text: string };

function entriesFromFlat(flat: Record<string, string>): Entry[] {
  const ordered: Entry[] = [];
  for (const e of LANGUAGE_ENTRIES) {
    const t = flat[e.code]?.trim();
    if (t) ordered.push({ code: e.code, text: t });
  }
  for (const [code, raw] of Object.entries(flat)) {
    if (knownCodes.has(code)) continue;
    const t = raw.trim();
    if (t) ordered.push({ code, text: t });
  }
  return ordered;
}

type MultilingualNameMapCellProps = {
  /** Localized strings keyed by book language id. */
  flat: Record<string, string>;
  /** Optional line above names (e.g. author honorific). */
  titlePrefix?: string;
  /** Two-column grid on wider screens — useful when many languages are shown in a card. */
  layout?: 'stack' | 'grid';
};

const flagImgSx = {
  width: 32,
  height: 20,
  objectFit: 'cover' as const,
  borderRadius: 0.5,
  border: 1,
  borderColor: 'divider',
  display: 'block',
  flexShrink: 0,
};

function LanguageCodeVisual({ code }: { code: string }) {
  const fn = getBookLanguageFlagFilename(code);
  if (fn) {
    return (
      <Box component="img" src={bookLanguageFlagPublicUrl(fn)} alt="" title={code} sx={flagImgSx} />
    );
  }
  return (
    <Chip
      label={code}
      size="small"
      variant="outlined"
      sx={{
        height: 22,
        flexShrink: 0,
        borderColor: 'divider',
        '& .MuiChip-label': { px: 0.75, fontSize: '0.65rem', fontWeight: 700 },
      }}
    />
  );
}

/**
 * Table cell content: name map as the main readable block — one row per language with a flag (or code chip fallback).
 */
function NameEntryRow({ item }: { item: Entry }) {
  return (
    <Stack
      direction="row"
      spacing={1}
      alignItems="flex-start"
      sx={{
        flexWrap: 'nowrap',
        columnGap: 1,
        minWidth: 0,
        width: '100%',
      }}
    >
      <LanguageCodeVisual code={item.code} />
      <Typography
        component="span"
        variant="body2"
        color="text.primary"
        sx={{
          minWidth: 0,
          flex: '1 1 auto',
          overflowWrap: 'anywhere',
          wordBreak: 'break-word',
          lineHeight: 1.45,
          pt: 0.125,
        }}
      >
        {item.text}
      </Typography>
    </Stack>
  );
}

export default function MultilingualNameMapCell({
  flat,
  titlePrefix,
  layout = 'stack',
}: MultilingualNameMapCellProps) {
  const entries = entriesFromFlat(flat);

  if (entries.length === 0 && !titlePrefix?.trim()) {
    return (
      <Typography variant="body2" color="text.disabled">
        —
      </Typography>
    );
  }

  const namesBlock =
    layout === 'grid' ? (
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
          columnGap: 2,
          rowGap: 0.75,
          width: '100%',
          minWidth: 0,
        }}
      >
        {entries.map((item) => (
          <NameEntryRow key={item.code} item={item} />
        ))}
      </Box>
    ) : (
      <Stack spacing={0.75} sx={{ width: '100%', minWidth: 0 }}>
        {entries.map((item) => (
          <NameEntryRow key={item.code} item={item} />
        ))}
      </Stack>
    );

  return (
    <Stack spacing={0.75} sx={{ py: 0.25, width: '100%', minWidth: 0 }}>
      {titlePrefix?.trim() ? (
        <Typography
          variant="caption"
          color="primary"
          fontWeight={600}
          sx={{ letterSpacing: '0.06em', textTransform: 'uppercase' }}
        >
          {titlePrefix.trim()}
        </Typography>
      ) : null}
      {namesBlock}
    </Stack>
  );
}
